import { useState, useEffect, useCallback } from 'react';
import { Subscription } from '../types';
import { ApiError } from '../utils/error-handler';
import { userService, organizationService, teamService, subscriptionService, paymentService, invitationService, roleService, contentService, tagService, searchService, authService } from '../../services';
import { extractErrorMessage } from '../utils/error-handler';
import { useOrganization } from '../context/OrganizationContext';

// Define interface for the API response
interface SubscriptionApiResponse {
  _id?: string;
  name?: string;
  plan?: string;
  organizationId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  trialEndDate?: string;
  teams?: Array<{ id: string; name: string }>;
  paymentMethodId?: string;
  billingDetails?: {
    paymentMethod?: string;
    lastFour?: string;
    billingAddress?: string;
    customerId?: string;
    subscriptionId?: string;
    paymentMethodId?: string;
  };
  addOns?: {
    extraUsers?: number;
    extraTeams?: number;
    extraStorage?: number;
    extraApiCalls?: number;
  };
  limits?: {
    teamsPerOrg?: number;
    usersPerOrg?: number;
    usersPerTeam?: number;
    storage?: number;
    apiCalls?: number;
  };
  usage?: {
    teamsCount?: number;
    activeUsers?: number;
    storageUsed?: number;
    apiCallsUsed?: number;
  };
  autoRenew?: boolean;
  nextBillingDate?: string;
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: any;
}

export function useSubscriptions(organizationId: string) {
  const { getOrganizationSubscriptions, refreshData } = useOrganization();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [pagination, setPagination] = useState<{
    totalCount: number;
    pageSize: number;
    currentPage: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  } | undefined>(undefined);

  const fetchSubscriptions = useCallback(async (forceRefresh = false, page = 1, limit = 100) => {
    if (!organizationId) {
      console.log(`🔍 useSubscriptions: No organizationId provided, skipping fetch`);
      return;
    }

    console.log(`🔍 useSubscriptions: fetchSubscriptions called for org ${organizationId}, forceRefresh=${forceRefresh}`);
    setIsLoading(true);
    setError(null);

    try {
      if (forceRefresh) {
        // Force refresh: always use direct API call
        console.log(`🔍 useSubscriptions: Force refreshing subscriptions from API for org ${organizationId}`);
        const result = await subscriptionService.getOrganizationSubscriptions(organizationId, { page, limit });
        console.log(`🔍 useSubscriptions: API returned ${result.subscriptions.length} subscriptions:`, result.subscriptions.map(s => ({
          id: s._id,
          name: s.name,
          organizationId: s.organizationId
        })));
        console.log(`🔍 useSubscriptions: Setting subscriptions state with ${result.subscriptions.length} items`);
        setSubscriptions(result.subscriptions as any);
        setPagination(result.pagination);
      } else {
        // Normal load: try cached data first
        console.log(`🔍 useSubscriptions: Checking cached subscriptions for org ${organizationId}`);
        const cachedSubscriptions = getOrganizationSubscriptions(organizationId);
        console.log(`🔍 useSubscriptions: Found ${cachedSubscriptions.length} cached subscriptions for org ${organizationId}`);

        if (cachedSubscriptions.length > 0) {
          console.log(`🔍 useSubscriptions: Using ${cachedSubscriptions.length} cached subscriptions`, cachedSubscriptions.map(s => ({ id: s._id, name: s.name })));
          setSubscriptions(cachedSubscriptions as any);
          setPagination(undefined); // No pagination info from cache
        } else {
          // Fallback to direct API call if no cached data
          console.log(`🔍 useSubscriptions: No cached subscriptions, fetching from API for org ${organizationId}`);
          const result = await subscriptionService.getOrganizationSubscriptions(organizationId, { page, limit });
          console.log(`🔍 useSubscriptions: API returned ${result.subscriptions.length} subscriptions`);
          console.log(`🔍 useSubscriptions: Setting subscriptions state with ${result.subscriptions.length} items`);
          setSubscriptions(result.subscriptions as any);
          setPagination(result.pagination);
        }
      }

      console.log(`🔍 useSubscriptions: fetchSubscriptions complete, isLoading will be set to false`);
    } catch (err) {
      console.error('🔍 useSubscriptions: Error getting subscriptions:', err);
      setError(extractErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, getOrganizationSubscriptions]);
  
  // Log when subscriptions state changes
  useEffect(() => {
    console.log(`🔍 useSubscriptions: subscriptions state changed, now have ${subscriptions.length} subscriptions`);
  }, [subscriptions]);

  useEffect(() => {
    console.log(`🔍 useSubscriptions: Initial mount or organizationId changed to ${organizationId}, calling fetchSubscriptions`);
    fetchSubscriptions();
  }, [fetchSubscriptions]);
  
  const createSubscription = async (data: {
    name: string;
    planId: string;
    autoRenew: boolean;
    organizationId: string;
  }) => {
    try {
      setIsLoading(true);
      const planData = {
        name: data.name,
        planId: data.planId,
        autoRenew: data.autoRenew,
        organizationId
      };

      await subscriptionService.createSubscription(planData);

      // Refresh global context data (updates subscriptions everywhere)
      await refreshData();

      await fetchSubscriptions(true); // Force refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const cancelSubscription = async (subscriptionId: string) => {
    try {
      setIsLoading(true);
      const cancelledSubscription = await subscriptionService.cancelSubscription(subscriptionId);

      // Immediately update the subscription in local state for instant UI feedback
      setSubscriptions(prevSubs =>
        prevSubs.map(sub =>
          (sub.id === subscriptionId || sub._id === subscriptionId)
            ? (cancelledSubscription as any)
            : sub
        )
      );

      // Refresh global context data in the background
      refreshData().catch(err => console.warn('Failed to refresh global context:', err));

      // Force refresh list in the background to ensure consistency
      fetchSubscriptions(true).catch(err => console.warn('Failed to refresh subscriptions:', err));

      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const reactivateSubscription = async (subscriptionId: string) => {
    try {
      setIsLoading(true);
      const reactivatedSubscription = await subscriptionService.reactivateSubscription(subscriptionId);

      // Immediately update the subscription in local state for instant UI feedback
      setSubscriptions(prevSubs =>
        prevSubs.map(sub =>
          (sub.id === subscriptionId || sub._id === subscriptionId)
            ? (reactivatedSubscription as any)
            : sub
        )
      );

      // Refresh global context data in the background
      refreshData().catch(err => console.warn('Failed to refresh global context:', err));

      // Force refresh list in the background to ensure consistency
      fetchSubscriptions(true).catch(err => console.warn('Failed to refresh subscriptions:', err));

      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const deleteSubscription = async (subscriptionId: string) => {
    try {
      setIsLoading(true);
      await subscriptionService.deleteSubscription(subscriptionId);

      // Refresh global context data
      await refreshData();

      await fetchSubscriptions(true); // Force refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscriptionAddOns = async (subscriptionId: string, addOns: { 
    extraUsers?: number; 
    extraStorage?: number;
    extraTeams?: number;
    extraApiCalls?: number
  }) => {
    try {
      setIsLoading(true);
      await subscriptionService.updateSubscriptionAddOns(subscriptionId, addOns as any);
      await fetchSubscriptions(); // Refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };
  
  const assignSubscriptionToTeam = async (teamId: string, subscriptionId: string) => {
    try {
      setIsLoading(true);
      await subscriptionService.assignSubscriptionToTeam(teamId, subscriptionId);

      // Refresh global context data (updates subscription usage and team associations everywhere)
      await refreshData();

      await fetchSubscriptions(true); // Force refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  const updateSubscriptionReferenceName = async (subscriptionId: string, referenceName: string) => {
    try {
      setIsLoading(true);
      await subscriptionService.updateSubscriptionReferenceName(subscriptionId, referenceName);
      await fetchSubscriptions(true); // Force refresh list
      return { success: true };
    } catch (err) {
      const error = extractErrorMessage(err);
      setError(error);
      return { success: false, error };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    subscriptions,
    pagination,
    isLoading,
    error,
    refreshSubscriptions: fetchSubscriptions,
    createSubscription,
    cancelSubscription,
    reactivateSubscription,
    deleteSubscription,
    updateSubscriptionAddOns,
    assignSubscriptionToTeam,
    updateSubscriptionReferenceName
  };
}

export default useSubscriptions;